import { useEffect } from "react";
import { baseUrl, routes } from "@/seo/routes";
// L'attribut lang du document appartient a LanguageProvider : sur /new et
// /game, la langue affichee n'est pas celle de la route.
import { OG_LOCALE, alternatesOf, langOf } from "@/seo/locale";
import { titleKey } from "@/i18n/language";
import { useLanguage } from "@/i18n/LanguageProvider";


/** Create or update a <meta> tag keyed by name/property attribute. */
function setMeta(attr, key, content) {
    if (content == null) return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

/**
 * Rejoue les balises hreflang de la route.
 *
 * Elles sont retirees puis reecrites a chaque changement de route : gardees,
 * celles de la page precedente annonceraient la traduction d'une autre page.
 */
function setAlternates(pairs) {
    document.head
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((node) => node.remove());
    for (const { hreflang, href } of pairs) {
        const el = document.createElement("link");
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", hreflang);
        el.setAttribute("href", href);
        document.head.appendChild(el);
    }
}

/** Create or update the canonical <link>. */
function setCanonical(href) {
    let el = document.head.querySelector('link[rel="canonical"]');
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

/**
 * Vrai tant que le document est celui que le serveur a livre, avec les
 * donnees structurees de son URL.
 *
 * Au niveau du module et non par instance : un <Seo> se demonte et se remonte
 * des que la page change de forme — la table des checkouts l'enveloppe dans
 * <Page>, une page de score le pose elle-meme, /game sans enveloppe. Une
 * instance neuve se croyait alors au premier rendu et gardait le balisage de
 * la page quittee.
 */
let documentFromServer = true;

/**
 * Per-route SEO. Looks up `path` in seoConfig and applies title, description,
 * canonical and Open Graph tags to the document head. Unknown paths (e.g. live
 * game pages) are marked noindex, as are routes flagged `noindex` in
 * seoConfig (e.g. /new, an app screen with no search intent of its own).
 *
 * Exception : les deux ecrans de l'application, /new et /game, prennent leur
 * titre et leur description dans le dictionnaire de l'interface plutot que dans
 * seoConfig, parce que leur langue se deduit au lieu d'etre portee par l'URL.
 *
 * This runs client-side; the postbuild prerender script bakes the same values
 * into the static HTML of each route for crawlers that do not execute JS.
 *
 * Il retire aussi les donnees structurees ecrites au build des que l'on change
 * de route sans recharger : elles decrivent la page d'arrivee sur l'URL, pas la
 * suivante. Un balisage absent vaut mieux qu'un balisage qui annonce une FAQ sur
 * la page des CGU. Le premier rendu, lui, est epargne : c'est celui que lisent
 * les crawlers qui executent le JS.
 *
 * `title` et `description` remplacent ceux de la route : les pages de score
 * calculent les leurs (voir seo/scorePages.js), la table n'en porte pas.
 */
export default function Seo({ path, noindex, title, description: pageDescription }) {
    const { t } = useLanguage();
    const meta = routes[path];
    const canonical = baseUrl + (path === "/" ? "/" : path);
    const isNoindex = noindex || !meta || meta.noindex === true;
    const lang = langOf(path);

    // Les deux ecrans de l'application tirent leur titre du dictionnaire, pas de
    // seoConfig : leur URL est unique et leur langue se deduit, donc une chaine
    // par route ne peut pas convenir. /new affichait un titre francais sous une
    // interface allemande, et /game, absent de seoConfig, n'en recevait aucun —
    // l'onglet gardait alors celui d'app.html, le titre marketing de l'accueil,
    // pendant toute la partie.
    const clef = titleKey(path);
    const titre = clef ? t(`seo.${clef}.title`) : title ?? meta?.title;
    const description = clef ? t(`seo.${clef}.description`) : pageDescription ?? meta?.description;

    useEffect(() => {
        if (titre) document.title = titre;
        setMeta("name", "description", description);
        setCanonical(canonical);
        setMeta(
            "name",
            "robots",
            isNoindex ? "noindex, follow" : "index, follow, max-image-preview:large"
        );
        setMeta("property", "og:title", titre);
        setMeta("property", "og:description", description);
        setMeta("property", "og:url", canonical);
        setMeta("property", "og:locale", OG_LOCALE[lang]);
        setAlternates(alternatesOf(path));

        if (documentFromServer) {
            documentFromServer = false;
            return;
        }
        document
            .querySelectorAll('script[data-jsonld="route"]')
            .forEach((node) => node.remove());
    }, [path, titre, description, canonical, isNoindex, lang]);

    return null;
}
